import { ColumnSort, SortingState } from "@tanstack/react-table";
import find from "lodash/find";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";
import last from "lodash/last";
import { OrderOption } from "PFTypes";

import { ApiOrderKeyToColumnNamesFormatter, ColumnNameToApiOrderKeyFormatter } from "./table.types";

export const getSelectedSort = (
  selectedApiOrder: OrderOption | undefined,
  parseMetaOrderName: ApiOrderKeyToColumnNamesFormatter
): ColumnSort[] => {
  if (!selectedApiOrder) {
    return [];
  }

  // in activity tables, some of the columns share common order key
  // for example: availability and start_date
  const selectedColumnIdsOrSelectedId = parseMetaOrderName(last(selectedApiOrder.name) as string);
  const selectedColumnIds = isArray(selectedColumnIdsOrSelectedId)
    ? selectedColumnIdsOrSelectedId
    : [selectedColumnIdsOrSelectedId];
  const desc = selectedApiOrder.direction === "desc";
  return selectedColumnIds.map((id) => ({ id, desc }));
};

const invertDirection = (direction: string) => (direction === "asc" ? "desc" : "asc");

type CalculateSortUpdateArgs = {
  newSorting: SortingState;
  defaultOrder: OrderOption | undefined;
  selectedApiOrder: OrderOption | undefined;
  options: OrderOption[];
  parseTableSortingId: ColumnNameToApiOrderKeyFormatter;
  parseMetaOrderName: ApiOrderKeyToColumnNamesFormatter;
};

// TODO: [SP-5799] this messy logic should be cleaned up and whole table sorting needs to be refactored
export const calculateOrderUpdate = ({
  newSorting,
  defaultOrder,
  selectedApiOrder,
  options,
  parseTableSortingId,
  parseMetaOrderName
}: CalculateSortUpdateArgs) => {
  const isNewOrderNone = newSorting.length === 0;
  const isCurrentOrderDefault =
    !!defaultOrder && !!selectedApiOrder && isEqual(selectedApiOrder, defaultOrder);

  if (isNewOrderNone && isCurrentOrderDefault && !!selectedApiOrder) {
    // When order changes from default order to [], the ordering doesn't change,
    // because API will provide default order for undefined order
    // To avoid unchanged order onSortingChange, [] order is skipped and current one is inverted
    return options.find(
      (option) =>
        isEqual(option.name, selectedApiOrder.name) &&
        option.direction === invertDirection(selectedApiOrder.direction)
    );
  }

  if (isNewOrderNone) {
    return undefined;
  }

  const { id, desc } = newSorting[0];
  let direction = desc ? "desc" : "asc";
  const orderKey = parseTableSortingId(id);
  const availableOptions = options.filter((option) => option.name.some((name) => name === orderKey)) || [];
  const selectedSorting = getSelectedSort(selectedApiOrder, parseMetaOrderName);
  const clickedAlreadySelectedSorting = find(selectedSorting, { id });

  // react-table sorting behaviour is inconsistent when more than one sorting is selected
  // in such a case clicking on one of selected columns
  // results in resetting direction back to initial toggle state always
  if (clickedAlreadySelectedSorting && selectedSorting.length > 1) {
    // it won't work if columns which can be selected together are numeric
    // or have `sortDescFirst` set to false
    if (clickedAlreadySelectedSorting.desc) {
      direction = "asc";
    } else {
      return undefined;
    }
  }

  if (availableOptions?.length === 0) {
    return undefined;
  }

  const matchingOption = availableOptions.find((option) => option.direction === direction);

  // case when user selects a column which does not have initial direction available as an option
  if (!clickedAlreadySelectedSorting && !matchingOption) {
    return availableOptions.find((option) => option.direction !== direction);
  }

  return matchingOption;
};
